"use client";
// ===================================================================
// Platform filter — the read behind the platform selector (DEC-0362).
//
// A platform is a domain of work (CRM, the virtual office, the AI agents,
// site & commerce, the factory). Choosing one narrows the app menu to the
// apps that belong to it, so a working session shows only what it needs —
// which is what RULE-platform-inheritance §7 says a platform is FOR.
//
// The one rule that matters here is FAIL OPEN. The App inventory is
// knowingly incomplete (Gal, 2026-08-31: "we clearly do not have all of our
// apps yet"), and only 35 of 86 rows carry a route at all. A route we
// cannot resolve to an App, or an App with no platform assignment, is
// therefore NOT evidence that it belongs elsewhere — it is absence of
// evidence, and hiding on it would make a live page unreachable through the
// menu. Both cases stay visible. Only a route that resolves to an App
// assigned to OTHER platforms is hidden.
//
// UNTAGGED is the counterpart Gal asked for (2026-08-31): fail-open makes the
// unassigned invisible AS a category — everything shows everywhere, so there
// is no way to go and find what nobody has decided yet. Selecting it inverts
// the filter and shows exactly that residue. It is COMPUTED, not a sixth
// platform: the five rows in vb_records were decided and seeded in DEC-0361
// and are read-only here, so the marker needs no row of its own.
// ===================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

/** localStorage key. Sits beside "cc-sidebar-filter" and the view-mode keys. */
export const PLATFORM_KEY = "cc-platform-filter";

/** The "no filter" sentinel. Never a platform slug. */
export const ALL_PLATFORMS = "all";

/**
 * The "show me only what nobody assigned" sentinel. Never a platform slug —
 * it is computed from the absence of one, so it needs no vb_records row.
 */
export const UNTAGGED = "untagged";

export interface Platform {
  id: string;
  slug: string;
  title: string;
  /**
   * How many Apps are assigned to this platform (Gal, 2026-09-01: "a number
   * next to each value, so you see how many App exist"). This is the whole
   * inventory, not the routed subset — an App is a development topic and
   * exists whether or not it has a screen yet, so the count says how much
   * work lives under the platform rather than how many tiles will appear.
   *
   * null means the aggregate is unavailable — the migration that creates
   * get_platform_app_counts has not been applied to this environment yet.
   * The badge then hides itself, because a chip printing 0 next to five
   * live Apps is a wrong number, and a wrong number is worse than none.
   */
  appCount: number | null;
}

interface PlatformCountRow {
  scope: "platform" | "all" | "untagged";
  platform_id: string | null;
  app_count: number;
  routed_app_count: number;
}

/** The two chips that are not platforms but still carry a number. */
export interface PlatformTotals {
  /** Every live App — NOT the sum of the platform columns, which double-counts. */
  all: number | null;
  /** Apps nobody assigned to any platform: the residue behind the dashed chip. */
  untagged: number | null;
}

interface NavPlatformRow {
  route: string;
  app_id: string;
  app_title: string;
  platform_slugs: string[];
}

async function fetchPlatforms(): Promise<{ platforms: Platform[]; totals: PlatformTotals }> {
  const { data, error } = await supabase.rpc("get_platform_registry");
  if (error) throw error;
  const rows = (data ?? []) as Omit<Platform, "appCount">[];

  // The counts live in their own aggregate so the registry RPC keeps its
  // identity-only shape. A failure here must not lose the selector: the
  // platforms are the feature, the badge is decoration. But the fallback is
  // null, never 0 — the aggregate is absent until its migration is applied,
  // and "no answer" printed as "zero Apps" is a lie the eye believes.
  const { data: countData } = await supabase.rpc("get_platform_app_counts");
  const countRows = (countData ?? []) as PlatformCountRow[];
  const countsAvailable = countRows.length > 0;

  const perPlatform = new Map(
    countRows
      .filter((r) => r.scope === "platform" && r.platform_id)
      .map((r) => [r.platform_id as string, Number(r.app_count)]),
  );
  // Once the aggregate answers, a platform missing from it genuinely holds
  // zero Apps — that 0 is a measurement and is shown.
  const scopeCount = (scope: PlatformCountRow["scope"]) =>
    countsAvailable
      ? Number(countRows.find((r) => r.scope === scope)?.app_count ?? 0)
      : null;

  return {
    platforms: rows.map((p) => ({
      ...p,
      appCount: countsAvailable ? (perPlatform.get(p.id) ?? 0) : null,
    })),
    totals: { all: scopeCount("all"), untagged: scopeCount("untagged") },
  };
}

async function fetchNavMap(): Promise<NavPlatformRow[]> {
  const { data, error } = await supabase.rpc("get_nav_platform_map");
  if (error) throw error;
  return (data ?? []) as NavPlatformRow[];
}

/**
 * Resolve a menu href to the App that owns it.
 *
 * Longest-prefix, matching the Rule 32 gate in check-new-route-has-app.mjs:
 * a screen under a registered App belongs to that App, so
 * /dashboard/entities/fields resolves to the /dashboard/entities row. Query
 * strings are dropped first — /dashboard/vcloud?tab=courses is a view of the
 * vcloud page, not an address of its own.
 *
 * Exported so the resolution rule can be tested without React.
 */
export function resolveRoute(href: string, routes: ReadonlySet<string>): string | null {
  const path = href.split("?")[0].split("#")[0];
  if (routes.has(path)) return path;
  const segs = path.split("/");
  // Stop at i > 2: "/dashboard" itself is a registered App, but falling back
  // to it would make every unregistered page inherit the dashboard's
  // platforms, which is a guess dressed up as data.
  for (let i = segs.length - 1; i > 2; i--) {
    const parent = segs.slice(0, i).join("/");
    if (routes.has(parent)) return parent;
  }
  return null;
}

function loadSelected(): string {
  if (typeof window === "undefined") return ALL_PLATFORMS;
  try {
    return localStorage.getItem(PLATFORM_KEY) || ALL_PLATFORMS;
  } catch {
    // Private windows and blocked site data throw on read. Default, don't crash.
    return ALL_PLATFORMS;
  }
}

export interface PlatformFilter {
  /** The five platforms, or [] while loading / on failure. */
  platforms: Platform[];
  /** Selected slug, or ALL_PLATFORMS. */
  selected: string;
  select: (slug: string) => void;
  /** True when a platform (or UNTAGGED) is chosen AND the map loaded. */
  isFiltering: boolean;
  /** Does this href survive the current selection? Fails open — see header. */
  matches: (href: string) => boolean;
  /** Platform slugs owning this href, for badges/tooltips. Empty if unknown. */
  slugsFor: (href: string) => string[];
  /** App counts for the two chips that are not platforms: "all" and UNTAGGED. */
  totals: PlatformTotals;
}

export function usePlatformFilter(): PlatformFilter {
  const [selected, setSelected] = useState<string>(ALL_PLATFORMS);

  // Read after mount: localStorage is not available during SSR, and reading it
  // in useState's initialiser would desync the server and client renders.
  useEffect(() => setSelected(loadSelected()), []);

  const { data: registry } = useQuery({
    queryKey: ["platform-registry"],
    queryFn: fetchPlatforms,
    staleTime: 5 * 60_000,
  });

  const { data: navMap } = useQuery({
    queryKey: ["nav-platform-map"],
    queryFn: fetchNavMap,
    staleTime: 60_000,
  });

  const select = useCallback((slug: string) => {
    setSelected(slug);
    try {
      localStorage.setItem(PLATFORM_KEY, slug);
    } catch {
      // Not persisting is a lost convenience, not a failure worth surfacing.
    }
  }, []);

  const { routeSet, slugsByRoute } = useMemo(() => {
    const set = new Set<string>();
    const byRoute = new Map<string, string[]>();
    for (const row of navMap ?? []) {
      set.add(row.route);
      byRoute.set(row.route, row.platform_slugs ?? []);
    }
    return { routeSet: set, slugsByRoute: byRoute };
  }, [navMap]);

  const slugsFor = useCallback(
    (href: string) => {
      const route = resolveRoute(href, routeSet);
      return route ? slugsByRoute.get(route) ?? [] : [];
    },
    [routeSet, slugsByRoute],
  );

  // Only filter once the map has actually arrived. Filtering against an empty
  // map would hide nothing (fail-open) but would flicker the menu on every
  // load; gating on it keeps the menu stable.
  const isFiltering = selected !== ALL_PLATFORMS && routeSet.size > 0;

  const matches = useCallback(
    (href: string) => {
      if (!isFiltering) return true;
      const slugs = slugsFor(href);
      // The residue: an unresolved route, or an App nobody assigned yet. Both
      // are "no decision has been made", which is exactly what UNTAGGED asks for.
      if (selected === UNTAGGED) return slugs.length === 0;
      // Unresolved route, or an App nobody assigned yet — see the header.
      if (slugs.length === 0) return true;
      return slugs.includes(selected);
    },
    [isFiltering, selected, slugsFor],
  );

  return {
    platforms: registry?.platforms ?? [],
    selected,
    select,
    isFiltering,
    matches,
    slugsFor,
    totals: registry?.totals ?? { all: null, untagged: null },
  };
}
